import React, { useEffect, useState, useRef } from 'react';
import { isPlatformWeb } from 'renative';

import isEqual from 'lodash/isEqual';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import Modal from '../../components/containers/Modal';

const noop = () => {};
const ModalContext = React.createContext({
    setModalChildren: noop,
    closeModal: noop,
    setModalProps: noop,
    setPreviousModal: noop,
});

export const ModalProvider = ({ children, ...props }) => {
    const [modalChildren, setChildren] = useState(null);
    const [modalProps, setProps] = useState({});
    const router = useRouter();
    const previousModalChildren = useRef(null);
    const onCloseModalRef = useRef(undefined);
    const isMounted = useRef(true);

    const setModalChildren = (modal) => {
        setChildren((prev) => {
            if (!previousModalChildren.current) {
                previousModalChildren.current = modal;
            }
            if (!isEqual(prev, modal)) {
                previousModalChildren.current = prev;
            }
            return modal;
        });
    };

    const setModalProps = (modal) => {
        setProps(modal);
        onCloseModalRef.current = modal?.onModalClose;
    };

    useEffect(
        () => () => {
            isMounted.current = false; // set flag component was unmounted
        },
        []
    );

    useEffect(() => {
        if (modalChildren) {
            setModalChildren(null);
            setModalProps({});
        }
    }, [isPlatformWeb && router.asPath]);

    const closeModal = () => {
        onCloseModalRef.current?.();
        setModalChildren(null);
        previousModalChildren.current = null;

        if (!isMounted.current) {
            return; // avoid update state update on an unmounted component
        }

        setModalProps({});
    };

    const onRequestClose = () => {
        closeModal();
    };

    const setPreviousModal = () => setModalChildren(previousModalChildren.current);

    return (
        <ModalContext.Provider
            value={{
                setModalChildren,
                closeModal,
                setModalProps,
                setPreviousModal,
                modalChildren,
            }}
        >
            {children}
            <Modal
                visible={!!modalChildren}
                setPreviousModal={setPreviousModal}
                {...props}
                {...modalProps}
                onRequestClose={onRequestClose}
            >
                {modalChildren}
            </Modal>
        </ModalContext.Provider>
    );
};

export const useModal = () => React.useContext(ModalContext);

export const withModal = (Component) => (props) =>
    (
        <ModalContext.Consumer>
            {(modalContext) => <Component {...props} {...modalContext} />}
        </ModalContext.Consumer>
    );
export { ModalContext };
